import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Surfaces that must stay usable and audible while a modal is open: the guided
// tutorial walks through modals, and toasts announce results.
const KEEP_INTERACTIVE_SELECTOR = ".tutorial-tooltip, .tutorial-backdrop-bg, .Toastify";
const LIVE_REGION_SELECTOR = '[aria-live], [role="status"], [role="alert"]';

const isVisible = (element) =>
  element.getAttribute("aria-hidden") !== "true" && !element.closest("[hidden]");

const getFocusableElements = (container) =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute("disabled") && isVisible(element)
  );

/**
 * Lands on the dialog's title, else its first control. Landing on a Close (X)
 * button makes screen readers announce "Close" before the user knows what the
 * dialog is. The container may wrap more than the dialog (e.g. a sidecar), so
 * the labelled dialog inside it is preferred.
 */
const getDefaultFocusTarget = (container) => {
  const dialog = container.hasAttribute("aria-labelledby")
    ? container
    : container.querySelector(
        '[role="dialog"][aria-labelledby], [role="alertdialog"][aria-labelledby]'
      ) ?? container;
  const titleId = dialog.getAttribute("aria-labelledby")?.split(/\s+/)[0];
  const title = titleId ? document.getElementById(titleId) : null;
  if (title && dialog.contains(title)) {
    if (!title.hasAttribute("tabindex")) {
      title.setAttribute("tabindex", "-1");
    }
    return title;
  }

  return getFocusableElements(dialog)[0] ?? null;
};

/** Marks everything outside the dialog inert; returns a function that undoes it. */
const makeBackgroundInert = (container) => {
  if (document.body.classList.contains("tutorial-lock")) {
    return () => {};
  }

  const changed = [];
  for (let node = container; node && node !== document.body; node = node.parentElement) {
    const parent = node.parentElement;
    if (!parent) {
      break;
    }
    for (const sibling of parent.children) {
      if (
        sibling === node ||
        sibling.inert ||
        ["SCRIPT", "STYLE", "LINK", "TEMPLATE"].includes(sibling.tagName) ||
        sibling.matches(LIVE_REGION_SELECTOR) ||
        sibling.matches(KEEP_INTERACTIVE_SELECTOR) ||
        sibling.querySelector(KEEP_INTERACTIVE_SELECTOR)
      ) {
        continue;
      }
      sibling.inert = true;
      changed.push(sibling);
    }
  }

  return () => {
    changed.forEach((element) => {
      element.inert = false;
    });
  };
};

export default function useFocusTrap(
  isOpen,
  containerRef,
  initialFocusRef,
  onEscape
) {
  const previousFocusRef = useRef(null);
  const onEscapeRef = useRef(onEscape);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const container = containerRef?.current;
    if (!container) {
      return undefined;
    }

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const restoreBackground = makeBackgroundInert(container);
    const targetElement = initialFocusRef?.current || getDefaultFocusTarget(container);
    targetElement?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && typeof onEscapeRef.current === "function") {
        event.preventDefault();
        onEscapeRef.current();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const currentFocusableElements = getFocusableElements(container);
      if (currentFocusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = currentFocusableElements[0];
      const lastElement =
        currentFocusableElements[currentFocusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey) {
        if (activeElement === firstElement || !container.contains(activeElement)) {
          event.preventDefault();
          lastElement.focus();
        }
        return;
      }

      if (activeElement === lastElement || !container.contains(activeElement)) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      restoreBackground();
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, containerRef, initialFocusRef]);
}
